import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { styled } from '@mui/material/styles';
import { Link, NavLink, useLocation, useParams } from 'react-router-dom';
import * as R from 'ramda';

import { getCurrentTab, getSubList, leftNavLinkIcon } from 'navigation/utils/nav';

const StyledLink = styled(NavLink)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
  },
  color: theme.palette.text.primary,
  display: 'block',
  height: 48,
  padding: theme.spacing(2, 0, 2, '54px'),
  textDecoration: 'none',
  width: '100%',
}));

const LinkActive = styled(NavLink)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
  },
  backgroundColor: theme.palette.secondary.light,
  color: theme.palette.text.link,
  display: 'block',
  fontWeight: 700,
  height: 48,
  padding: theme.spacing(2, 0, 2, '54px'),
  textDecoration: 'none',
  width: '100%',
}));

const LinkDisabled = styled(NavLink)(({ theme }) => ({
  '&:hover': {
    backgroundColor: 'transparent',
  },
  color: theme.palette.text.disabled,
  cursor: 'not-allowed',
  display: 'block',
  height: 48,
  padding: theme.spacing(2, 0, 2, '54px'),
  textDecoration: 'none',
  width: '100%',
}));

const RouteAccordion = ({ id, isNavDrawerOpen, label, route, subRoutes, setIsNavDrawerOpen }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const { pathname } = useLocation();
  const { currentAffiliateId, dataCompanyId } = useParams();
  const baseRoute = `/affiliate/${currentAffiliateId}/${dataCompanyId}`;
  const adjustedPath = pathname.split('/').slice(4).join('/');
  const currentTab = getCurrentTab(adjustedPath);

  // Sub List Controls
  const openSubList = getSubList(adjustedPath, currentTab.subRoutes);
  const openSubListId = openSubList?.id;

  // State value required for user input
  const [currentOpenSubList, setCurrentOpenSubList] = useState(openSubListId);

  // changes the sub list based on url
  useEffect(() => {
    setCurrentOpenSubList(openSubListId);
  }, [openSubListId]);

  // reset current open sub list if closing drawer
  useEffect(() => {
    !isNavDrawerOpen && setCurrentOpenSubList(openSubListId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNavDrawerOpen]);

  // reset everything if cycling between views
  useEffect(() => {
    setCurrentOpenSubList(openSubListId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lgUp]);

  const currentLinkSelected = id === currentOpenSubList;

  // changes the sub list based on user input
  const handleClickSubList = idx =>
    idx !== currentOpenSubList ? setCurrentOpenSubList(idx) : setCurrentOpenSubList(undefined);

  return subRoutes.length ? (
    <Accordion
      className="heap-button"
      data-testid="accordion"
      disableGutters
      expanded={currentLinkSelected}
      onChange={() => handleClickSubList(id)}
      sx={{
        '&.Mui-expanded': {
          backgroundColor: 'surface.white',
          borderRadius: 0.5,
          boxShadow: 6,
          zIndex: 1,
        },
        '&:before': {
          backgroundColor: 'transparent', // gets rid of fainted lines underneath each panel
        },
        backgroundColor: 'graphic.lighter',
        borderBottom: 'none',
        boxShadow: 'none',
        width: '100%',
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreRounded
            sx={{ color: currentLinkSelected ? 'primary.main' : 'secondary.medium' }}
          />
        }
        sx={{
          '&.Mui-expanded': {
            maxHeight: 48,
            minHeight: 'unset',
            px: 1,
            py: 0,
          },
          '&:hover': {
            borderTopLeftRadius: 0.5,
            borderTopRightRadius: 0.5,
          },
          alignItems: 'center',
          px: 2,
          py: 0,
        }}
      >
        <Box
          sx={{
            '& > svg': {
              color: currentLinkSelected ? 'secondary.main' : 'primary.light',
            },
            alignItems: 'center',
            display: 'flex',
            minWidth: '37px',
            width: '37px',
          }}
        >
          {leftNavLinkIcon(label, id, currentOpenSubList)}
        </Box>
        <ListItemText
          primary={label}
          sx={{
            '& > &:focus': {
              backgroundColor: 'transparent',
            },
            '& > span': {
              color: 'secondary.main',
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '0.25px',
              lineHeight: '18px',
            },
            backgroundColor: 'transparent',
          }}
        />
      </AccordionSummary>
      <AccordionDetails
        sx={{
          alignItems: 'center',
          flexDirection: 'column',
          padding: 0,
        }}
      >
        {subRoutes.map(({ disabled, id: i, label: l, route: subRoute }) =>
          disabled ? (
            <LinkDisabled key={i}>{l}</LinkDisabled>
          ) : R.includes(subRoute, pathname) ? (
            <LinkActive
              key={i}
              onClick={e => (disabled ? e.preventDefault() : setIsNavDrawerOpen(false))}
              to={`${baseRoute}/${subRoute}`}
            >
              {l}
            </LinkActive>
          ) : (
            <StyledLink
              key={i}
              onClick={e => (disabled ? e.preventDefault() : setIsNavDrawerOpen(false))}
              to={`${baseRoute}/${subRoute}`}
            >
              {l}
            </StyledLink>
          )
        )}
      </AccordionDetails>
    </Accordion>
  ) : (
    <Link
      className="heap-button"
      onClick={() => setIsNavDrawerOpen(false)}
      style={{
        display: 'flex',
        margin: '12px 0',
        padding: '0 16px',
        textDecoration: 'none',
        width: '100%',
      }}
      to={`${baseRoute}/${route}`}
    >
      <Box
        sx={{
          '& > svg': {
            color: 'primary.light',
          },
          alignItems: 'center',
          display: 'flex',
          minWidth: '37px',
          width: '37px',
        }}
      >
        {leftNavLinkIcon(label, id, currentOpenSubList)}
      </Box>
      <ListItemText
        primary={label}
        sx={{
          '& > &:focus': {
            backgroundColor: 'transparent',
          },
          '& > span': {
            color: 'secondary.main',
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: '0.25px',
            lineHeight: '18px',
          },
          backgroundColor: 'transparent',
        }}
      />
    </Link>
  );
};

RouteAccordion.propTypes = {
  id: number.isRequired,
  isNavDrawerOpen: bool.isRequired,
  label: string.isRequired,
  route: string.isRequired,
  setIsNavDrawerOpen: func.isRequired,
  subRoutes: arrayOf(shape({})),
};

RouteAccordion.defaultProps = {
  subRoutes: [],
};

export default RouteAccordion;
